<template>
    <div class="card thick-border">
        <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-2">
                <h5 style="margin:0">🔄 DEVICE ROTATOR</h5>
                <button type="button" class="btn btn-sm btn-outline-primary" @click="showTutorial = true">
                    <i class="fas fa-question-circle"></i> Lihat Tutorial
                </button>
            </div>
            <p class="text-muted small mb-3" style="text-align:center">
                Distribusi pesan ke beberapa device untuk hindari banned. Pesan dibagi otomatis sesuai limit per device.
            </p>

            <!-- Mode -->
            <label>Mode Distribusi</label>
            <select v-model="config.mode" @change="emit" class="form-control mb-3">
                <option value="batch">📦 Sequential (Device A habis dulu, baru Device B)</option>
                <option value="round_robin">🔁 Round-Robin (Pesan 1→A, Pesan 2→B, Pesan 3→A...)</option>
            </select>
            <small class="form-text text-muted mb-2">
                <i class="fas fa-info-circle"></i>
                <b>Sequential</b>: aman untuk warming-up. <b>Round-Robin</b>: distribusi paling merata, terbaik cegah banned.
            </small>

            <hr>

            <label>Pilih Device & Set Limit per Device</label>
            <div v-if="!loadingDevices && connectedDevices.length === 0" class="alert alert-warning mb-0">
                <i class="fas fa-exclamation-triangle"></i> Tidak ada device yang terkoneksi. Connect minimal 2 device dulu untuk pakai rotator.
            </div>
            <div v-else-if="loadingDevices" class="text-center text-muted py-2">
                <i class="fas fa-spinner fa-spin"></i> Memuat device...
            </div>
            <table v-else class="table table-sm rotator-table">
                <thead>
                    <tr>
                        <th width="40px">Pilih</th>
                        <th>Device</th>
                        <th width="180px">Limit Pesan</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="d in connectedDevices" :key="d.id"
                        :class="{ 'row-selected': isSelected(d.id) }">
                        <td>
                            <input type="checkbox" :checked="isSelected(d.id)" @change="toggleDevice(d)" />
                        </td>
                        <td>
                            <b>{{ d.name }}</b><br>
                            <small class="text-muted">{{ d.no_hp || '-' }} · <span class="badge badge-success">connected</span></small>
                        </td>
                        <td>
                            <input type="number" v-model.number="limits[d.id]" @input="emit"
                                :disabled="!isSelected(d.id)"
                                class="form-control form-control-sm" min="1" placeholder="cth: 50" />
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- Summary -->
            <div class="alert alert-info mb-0 mt-2" v-if="selectedCount > 0" style="font-size: 13px;">
                <b>📊 Ringkasan Konfigurasi:</b><br>
                ✅ <b>{{ selectedCount }}</b> device aktif<br>
                📨 Total kapasitas: <b>{{ totalCapacity }}</b> pesan per blast<br>
                🔀 Mode: <b>{{ config.mode === 'batch' ? 'Sequential' : 'Round-Robin' }}</b>
                <hr style="margin: 8px 0">
                <small>
                    <i class="fas fa-shield-alt"></i> <b>Tips Anti-Banned:</b>
                    Set limit per device <b>max 50-100</b> pesan/hari, kombinasikan dengan delay antar pesan minimal 30 detik
                    + variasi pesan pakai <code>(Halo|Hi|Hai)</code>.
                </small>
            </div>
        </div>

        <!-- TUTORIAL MODAL -->
        <div v-if="showTutorial" class="dr-modal-backdrop" @click.self="showTutorial = false">
            <div class="dr-modal">
                <div class="dr-modal-header">
                    <h4 style="margin:0">📚 Tutorial Device Rotator</h4>
                    <button type="button" class="dr-close-btn" @click="showTutorial = false">×</button>
                </div>
                <div class="dr-modal-body">

                    <div class="dr-hero">
                        <h5 style="margin:0; color:#fff">🔄 Anti-Banned dengan Device Rotator</h5>
                        <p style="margin:6px 0 0; opacity:.95; font-size:13px">
                            Distribusi pesan ke beberapa device WhatsApp sekaligus untuk mengurangi risiko banned.
                        </p>
                    </div>

                    <div class="dr-warn">
                        <b>⚠️ Fakta:</b> Akun WA umur 7+ tahun pun bisa kena pembatasan kalau pola pengiriman terdeteksi spam (misal kirim 80+ pesan dalam waktu cepat). WA tidak lihat umur akun, tapi <b>pola perilaku terkini</b>.
                    </div>

                    <h5 class="dr-section-title">Langkah-Langkah</h5>

                    <div class="dr-step">
                        <div class="dr-step-head"><span class="dr-step-num">1</span> Buka Menu Kirim Pesan</div>
                        <p>Login ke akun StarSender. Di sidebar kiri, klik <b>Pesan Keluar → Kirim Pesan</b>.</p>
                        <img src="https://app.starsender.online/img/tutorial/device-rotator/01-menu-kirim-pesan.png" alt="Menu Kirim Pesan">
                    </div>

                    <div class="dr-step">
                        <div class="dr-step-head"><span class="dr-step-num">2</span> Pilih Tab Blast atau CSV</div>
                        <p>Device Rotator tersedia di 2 tab: <b>Kirim Blast</b> (broadcast ke kontak/group) dan <b>Kirim Dengan CSV</b> (upload file).</p>
                        <img src="https://app.starsender.online/img/tutorial/device-rotator/02-tab-blast-csv.png" alt="Tab Blast & CSV">
                    </div>

                    <div class="dr-step">
                        <div class="dr-step-head"><span class="dr-step-num">3</span> Aktifkan Device Rotator</div>
                        <p>Centang checkbox <b>Aktifkan Device Rotator</b> dengan badge <b>ANTI-BANNED 🛡️</b>.</p>
                        <img src="https://app.starsender.online/img/tutorial/device-rotator/03-toggle-rotator.png" alt="Toggle Rotator">
                    </div>

                    <div class="dr-step">
                        <div class="dr-step-head"><span class="dr-step-num">4</span> Pilih Mode Distribusi</div>
                        <div class="dr-mode-card">
                            <b>🔁 Round-Robin <span style="color:#43a047">(Direkomendasikan)</span></b>
                            <p style="margin:4px 0; font-size:12px">Pesan 1 → Device A · Pesan 2 → Device B · Pesan 3 → Device C · ...</p>
                            <small style="color:#2e7d32">✅ Paling aman cegah banned, distribusi merata</small>
                        </div>
                        <div class="dr-mode-card">
                            <b>📦 Sequential</b>
                            <p style="margin:4px 0; font-size:12px">Device A penuh dulu (limit 50) → lanjut Device B → lanjut Device C</p>
                            <small style="color:#f57c00">💡 Cocok untuk warming-up bertahap</small>
                        </div>
                        <img src="https://app.starsender.online/img/tutorial/device-rotator/04-mode-dropdown.png" alt="Mode Dropdown">
                    </div>

                    <div class="dr-step">
                        <div class="dr-step-head"><span class="dr-step-num">5</span> Centang Device & Set Limit</div>
                        <p>Centang device yang dipakai, lalu isi <b>Limit Pesan</b> per device.</p>
                        <table class="dr-limit-table">
                            <thead><tr><th>Tipe Akun WA</th><th>Limit Aman/Hari</th></tr></thead>
                            <tbody>
                                <tr><td>Akun baru (&lt;1 bulan)</td><td><b>10-20 pesan</b></td></tr>
                                <tr><td>Akun normal (1-12 bulan)</td><td><b>50-100 pesan</b></td></tr>
                                <tr><td>Akun matang (1+ tahun)</td><td><b>100-200 pesan</b></td></tr>
                                <tr><td>Pernah kena restrict</td><td><b>10 pesan, naikkan bertahap</b></td></tr>
                            </tbody>
                        </table>
                        <img src="https://app.starsender.online/img/tutorial/device-rotator/05-pilih-device.png" alt="Tabel Device">
                    </div>

                    <div class="dr-step">
                        <div class="dr-step-head"><span class="dr-step-num">6</span> Cek Ringkasan Konfigurasi</div>
                        <p>Pastikan <b>Total Kapasitas</b> ≥ jumlah target broadcast Anda.</p>
                        <img src="https://app.starsender.online/img/tutorial/device-rotator/06-ringkasan.png" alt="Ringkasan">
                    </div>

                    <div class="dr-step">
                        <div class="dr-step-head"><span class="dr-step-num">7</span> Lengkapi Pesan & Klik Kirim</div>
                        <ul style="margin:0; padding-left:18px">
                            <li>Pilih tujuan (kontak/group/CSV)</li>
                            <li>Tulis pesan, pakai <code>{nama}</code> dan random text <code>(Halo|Hi|Hai)</code></li>
                            <li>Set delay interval minimal 30-60 detik</li>
                            <li>Klik <b>Kirim Pesan</b></li>
                        </ul>
                        <img src="https://app.starsender.online/img/tutorial/device-rotator/07-final-kirim.png" alt="Tombol Kirim">
                    </div>

                    <h5 class="dr-section-title">🛡️ Tips Anti-Banned</h5>
                    <ol style="font-size:13px">
                        <li>Variasi pesan pakai <code>(Halo|Hi|Hai|Selamat pagi)</code></li>
                        <li>Personalisasi dengan <code>{nama}</code></li>
                        <li>Delay antar pesan minimal <b>30 detik</b> (idealnya 60-120 detik)</li>
                        <li>Hindari kata trigger spam (PROMO!! KLIK LINK, dll)</li>
                        <li>Pastikan kontak target sudah saving nomor Anda</li>
                        <li>Hindari login WA Web/Desktop di banyak browser bersamaan</li>
                        <li>Warming-up bertahap untuk akun baru (10-20/hari → naikkan)</li>
                    </ol>

                    <h5 class="dr-section-title">❓ FAQ</h5>
                    <div class="dr-faq">
                        <p><b>Q: Berapa minimum device?</b><br>
                        A: Minimal 2 device connected. Idealnya 3-5 untuk distribusi optimal.</p>
                    </div>
                    <div class="dr-faq">
                        <p><b>Q: Apa bedanya dengan Multiple Jadwal?</b><br>
                        A: Multiple Jadwal = beda waktu. Device Rotator = beda device di waktu sama.</p>
                    </div>
                    <div class="dr-faq">
                        <p><b>Q: Apakah ada biaya tambahan?</b><br>
                        A: Tidak. Setiap pesan tetap pakai kuota paket Anda, tidak dikalikan.</p>
                    </div>
                    <div class="dr-faq">
                        <p><b>Q: Variabel <code>{nama}</code> dan random text masih jalan?</b><br>
                        A: Ya, tetap berfungsi normal di semua device.</p>
                    </div>

                    <div class="dr-info">
                        💬 Butuh bantuan? Hubungi <b>+62 813-1922-7392</b> (Senin-Jumat 10:00-20:00 WIB) atau email <b>starfield.id@gmail.com</b>.
                    </div>
                </div>
                <div class="dr-modal-footer">
                    <button type="button" class="btn btn-primary" @click="showTutorial = false">
                        Sudah Paham, Tutup
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'DeviceRotator',
    props: {
        value: {
            type: Object,
            default: () => ({ mode: 'round_robin', devices: [] }),
        },
        deviceList: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            loadingDevices: false,
            allDevices: [],
            limits: {},
            showTutorial: false,
            config: {
                mode: this.value.mode || 'round_robin',
                devices: [...(this.value.devices || [])],
            },
        };
    },
    computed: {
        connectedDevices() {
            const list = this.deviceList && this.deviceList.length ? this.deviceList : this.allDevices;
            return list.filter(d => (d.status || '').toLowerCase() === 'connected');
        },
        selectedCount() {
            return this.config.devices.length;
        },
        totalCapacity() {
            return this.config.devices.reduce((sum, d) => sum + (parseInt(d.limit) || 0), 0);
        },
    },
    async mounted() {
        if (!this.deviceList || this.deviceList.length === 0) {
            await this.loadDevices();
        }
        // Restore limits state from initial value
        (this.value.devices || []).forEach(d => {
            this.$set(this.limits, d.id, d.limit);
        });
    },
    methods: {
        async loadDevices() {
            this.loadingDevices = true;
            try {
                const r = await this.$store.dispatch("device_list_all");
                this.allDevices = r.data.data.devices || [];
            } catch (err) {
                console.error("Gagal load devices:", err);
            } finally {
                this.loadingDevices = false;
            }
        },
        isSelected(id) {
            return this.config.devices.some(d => d.id === id);
        },
        toggleDevice(d) {
            const idx = this.config.devices.findIndex(x => x.id === d.id);
            if (idx >= 0) {
                this.config.devices.splice(idx, 1);
                this.$set(this.limits, d.id, undefined);
            } else {
                const defaultLimit = this.limits[d.id] || 50;
                this.config.devices.push({ id: d.id, name: d.name, limit: defaultLimit });
                this.$set(this.limits, d.id, defaultLimit);
            }
            this.emit();
        },
        emit() {
            // Sync limits ke config.devices
            this.config.devices = this.config.devices.map(d => ({
                ...d,
                limit: parseInt(this.limits[d.id]) || 50,
            }));
            this.$emit('input', { mode: this.config.mode, devices: this.config.devices });
            this.$emit('change', { mode: this.config.mode, devices: this.config.devices });
        },
    },
};
</script>

<style scoped>
.thick-border {
    border: 2px solid #1976d2 !important;
    border-radius: 8px;
}
.rotator-table th {
    font-size: 12px;
    color: #5a6a7c;
    background: #f6faff;
}
.rotator-table tr.row-selected {
    background: #e3f2fd;
}

/* TUTORIAL MODAL */
.dr-modal-backdrop {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 20px;
}
.dr-modal {
    background: #fff;
    border-radius: 12px;
    max-width: 760px;
    width: 100%;
    max-height: 92vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 12px 48px rgba(0,0,0,0.3);
}
.dr-modal-header {
    padding: 16px 22px;
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
    color: #fff;
    border-radius: 12px 12px 0 0;
}
.dr-close-btn {
    background: rgba(255,255,255,0.2);
    color: #fff;
    border: none;
    font-size: 26px;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    cursor: pointer;
    line-height: 1;
}
.dr-close-btn:hover { background: rgba(255,255,255,0.35); }
.dr-modal-body {
    padding: 22px 26px;
    overflow-y: auto;
    flex: 1;
    color: #2c3e50;
    line-height: 1.6;
}
.dr-modal-footer {
    padding: 14px 22px;
    border-top: 1px solid #e0e0e0;
    text-align: right;
}
.dr-hero {
    background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
    color: #fff;
    padding: 18px 20px;
    border-radius: 8px;
    margin-bottom: 16px;
    text-align: center;
}
.dr-warn {
    background: #fff3e0;
    border-left: 4px solid #f57c00;
    padding: 12px 16px;
    border-radius: 6px;
    margin-bottom: 18px;
    font-size: 13px;
}
.dr-info {
    background: #e3f2fd;
    border-left: 4px solid #1976d2;
    padding: 12px 16px;
    border-radius: 6px;
    margin-top: 18px;
    font-size: 13px;
}
.dr-section-title {
    color: #1565c0;
    margin-top: 24px;
    padding-left: 10px;
    border-left: 4px solid #42a5f5;
    font-size: 17px;
}
.dr-step {
    background: #f6faff;
    border: 1px solid #cfd8dc;
    border-radius: 8px;
    padding: 14px 18px;
    margin: 12px 0;
}
.dr-step-head {
    font-size: 16px;
    font-weight: 600;
    color: #0d47a1;
    margin-bottom: 8px;
}
.dr-step-num {
    display: inline-block;
    background: #1976d2;
    color: #fff;
    width: 28px;
    height: 28px;
    line-height: 28px;
    text-align: center;
    border-radius: 50%;
    margin-right: 8px;
    font-weight: 700;
}
.dr-step img {
    max-width: 100%;
    height: auto;
    border: 1px solid #cfd8dc;
    border-radius: 6px;
    margin-top: 10px;
    display: block;
    box-shadow: 0 2px 6px rgba(0,0,0,0.08);
}
.dr-mode-card {
    background: #fff;
    border: 1px solid #1976d2;
    border-radius: 8px;
    padding: 12px 14px;
    margin: 8px 0;
    font-size: 13px;
}
.dr-limit-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    margin: 10px 0;
}
.dr-limit-table th {
    background: #1976d2;
    color: #fff;
    padding: 8px 12px;
    text-align: left;
    font-size: 12px;
}
.dr-limit-table td {
    padding: 6px 12px;
    border-bottom: 1px solid #e0e0e0;
}
.dr-limit-table tr:nth-child(even) td { background: #f6faff; }
.dr-faq {
    background: #fff;
    border-left: 3px solid #42a5f5;
    padding: 10px 14px;
    margin: 8px 0;
    border-radius: 4px;
    font-size: 13px;
}
.dr-faq p { margin: 0; }
.dr-modal-body code {
    background: #e3f2fd;
    color: #0d47a1;
    padding: 1px 6px;
    border-radius: 3px;
    font-size: 12px;
}
</style>
